// customMessageClient.js
// Client-side utilities for custom WebSocket messages (non-Yjs messages)
// This allows custom messages to coexist with Yjs protocol on the same WebSocket

// Message type constant (must match server)
export const MESSAGE_TYPE_CUSTOM = 99;

/**
 * Encode a custom message with type prefix
 * @param {Object} data - Data to encode
 * @returns {Uint8Array} Encoded message with type prefix
 */
export function encodeCustomMessage(data) {
    const jsonStr = JSON.stringify(data);
    const encoder = new TextEncoder();
    const jsonBytes = encoder.encode(jsonStr);

    // Create buffer with room for type byte + JSON
    const buffer = new Uint8Array(1 + jsonBytes.length);
    buffer[0] = MESSAGE_TYPE_CUSTOM;
    buffer.set(jsonBytes, 1);

    return buffer;
}

/**
 * Decode a custom message (assumes type prefix has been verified)
 * @param {Uint8Array} buffer - Buffer to decode
 * @returns {Object} Decoded data
 */
export function decodeCustomMessage(buffer) {
    const decoder = new TextDecoder();
    const jsonStr = decoder.decode(buffer.slice(1));
    return JSON.parse(jsonStr);
}

/**
 * Check if a message is a custom message
 * @param {Uint8Array} buffer - Buffer to check
 * @returns {boolean} True if this is a custom message
 */
export function isCustomMessage(buffer) {
    return buffer && buffer.length > 0 && buffer[0] === MESSAGE_TYPE_CUSTOM;
}

/**
 * Setup a handler for custom messages on a WebSocket
 * This intercepts custom messages before they reach y-websocket
 * @param {WebSocket} ws - WebSocket instance
 * @param {Function} handler - Handler function that receives decoded data
 * @returns {Function} Cleanup function to remove the listener
 */
export function setupCustomMessageHandler(ws, handler) {
    const listener = async (event) => {
        // Only process binary messages
        if (!(event.data instanceof Blob) && !(event.data instanceof ArrayBuffer)) {
            return;
        }

        // Convert to Uint8Array
        let buffer;
        if (event.data instanceof Blob) {
            const arrayBuffer = await event.data.arrayBuffer();
            buffer = new Uint8Array(arrayBuffer);
        } else {
            buffer = new Uint8Array(event.data);
        }

        // Check if it's a custom message
        if (isCustomMessage(buffer)) {
            try {
                const data = decodeCustomMessage(buffer);
                console.log('[custom-message] Received:', data.type);
                handler(data);
                // Prevent y-websocket from processing this message
                event.stopImmediatePropagation();
            } catch (error) {
                console.error('[custom-message] Failed to decode:', error);
            }
        }
    };

    // Add listener with capture phase to intercept before y-websocket
    ws.addEventListener('message', listener, {capture: true});

    // Return cleanup function
    return () => ws.removeEventListener('message', listener, {capture: true});
}

/**
 * Image upload handler class for managing image uploads through WebSocket
 */
export class ImageUploadHandler {
    constructor(ws) {
        this.ws = ws;
        this.pendingUploads = new Map();

        // Setup handler for responses
        this.cleanup = setupCustomMessageHandler(ws, (data) => {
            this.handleMessage(data);
        });
    }

    handleMessage(data) {
        if (data.type === 'image-upload-success' || data.type === 'image-upload-error') {
            const pending = this.pendingUploads.get(data.uploadId);
            if (pending) {
                this.pendingUploads.delete(data.uploadId);
                if (data.type === 'image-upload-success') {
                    console.log('[custom-message] Image upload successful:', data.url);
                    pending.resolve(data.url);
                } else {
                    console.error('[custom-message] Image upload failed:', data.error);
                    pending.reject(new Error(data.error || 'Upload failed'));
                }
            }
        }
    }

    async uploadImage(file) {
        // Convert file to base64
        const base64Data = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const result = reader.result;
                // Remove data URL prefix (e.g., "data:image/png;base64,")
                const base64 = result.split(',')[1];
                resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });

        const uploadId = `upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        // Create promise for response
        const responsePromise = new Promise((resolve, reject) => {
            this.pendingUploads.set(uploadId, {resolve, reject});

            // Set timeout
            setTimeout(() => {
                if (this.pendingUploads.has(uploadId)) {
                    this.pendingUploads.delete(uploadId);
                    reject(new Error('Upload timeout'));
                }
            }, 30000); // 30 second timeout
        });

        // Send upload request with custom message encoding
        const message = encodeCustomMessage({
            type: 'image-upload',
            uploadId,
            filename: file.name,
            data: base64Data,
            size: file.size,
            mimeType: file.type,
        });

        console.log('[custom-message] Sending image upload request:', uploadId);
        this.ws.send(message);

        return responsePromise;
    }

    destroy() {
        if (this.cleanup) {
            this.cleanup();
        }
        this.pendingUploads.clear();
    }
}

